import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, Sparkles, IndianRupee, Info, RefreshCw, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Care-portal pricing table (LCO / Distributor view).
 *
 * Rules:
 *  - Every row shows the caller's own cost (`your_cost`) so they know
 *    their floor.
 *  - `customer_price` is the field they edit — what an end subscriber
 *    pays inside /care.
 *  - If left blank, /care renewals for that plan are BLOCKED (no fallback).
 *  - "Apply +15%" quick-fill helps LCOs set a sensible default across
 *    all rows in one click.
 */
export default function CarePricing() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});      // package_id → string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gstRate, setGstRate] = useState(0.18);
  const token = localStorage.getItem('portal_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/care-pricing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load');
      setRows(data.packages || []);
      setGstRate(data.gst_rate ?? 0.18);
      const d = {};
      (data.packages || []).forEach(p => {
        if (p.customer_price != null) d[p.package_id] = String(p.customer_price);
      });
      setDrafts(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (pid, v) => setDrafts(d => ({ ...d, [pid]: v.replace(/[^0-9.]/g, '') }));

  const applyDefault15pct = () => {
    const next = { ...drafts };
    rows.forEach(r => {
      if (r.your_cost > 0) next[r.package_id] = (r.your_cost * 1.15).toFixed(2);
    });
    setDrafts(next);
    toast.success('Applied +15% markup — review then click Save all');
  };

  const dirtyItems = useMemo(() => {
    const out = [];
    rows.forEach(r => {
      const v = drafts[r.package_id];
      if (v === '' || v == null) return;
      const num = parseFloat(v);
      if (Number.isFinite(num) && (r.customer_price == null || Math.abs(num - r.customer_price) >= 0.005)) {
        out.push({ package_id: r.package_id, price: num });
      }
    });
    return out;
  }, [drafts, rows]);

  const saveAll = async () => {
    if (dirtyItems.length === 0) return toast.info('Nothing changed');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/care-pricing/bulk-set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prices: dirtyItems }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(`Saved ${data.modified} price${data.modified === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveOne = async (pid) => {
    const v = drafts[pid];
    const num = parseFloat(v);
    if (!Number.isFinite(num) || num < 0) return toast.error('Enter a valid price');
    try {
      const res = await fetch(`${API}/api/portal/iptv/care-pricing/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: pid, price: num }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success('Price saved');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal/iptv')} data-testid="care-pricing-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="text-sm font-semibold text-slate-800">Customer Care-Portal Pricing</div>
        </div>
      </header>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-indigo-600" />
              Customer Care-Portal Pricing
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Set what your subscribers pay when they self-renew on the ANT TV Care
              portal. Plans without a price stay <span className="font-medium">hidden</span> from your customers — set at least one to enable self-recharge.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={applyDefault15pct} data-testid="care-price-apply-15">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Apply +15% markup
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="care-price-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={saveAll} disabled={saving || dirtyItems.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="care-price-save-all">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save all ({dirtyItems.length})
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
              <Info className="w-4 h-4 text-slate-400" />
              GST rate applied to subscriber payments: {(gstRate * 100).toFixed(0)}% (inclusive)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-slate-500 flex items-center gap-2 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading plans…
              </div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No IPTV packages configured yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2">Plan</th>
                      <th className="text-right px-4 py-2">Your cost</th>
                      <th className="text-right px-4 py-2">Customer price</th>
                      <th className="text-right px-4 py-2">Margin</th>
                      <th className="text-right px-4 py-2">Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const draft = drafts[r.package_id];
                      const draftNum = parseFloat(draft);
                      const draftMargin = Number.isFinite(draftNum) && r.your_cost > 0
                        ? draftNum - r.your_cost : null;
                      const marginPct = draftMargin != null && r.your_cost > 0
                        ? (draftMargin / r.your_cost) * 100 : null;
                      return (
                        <tr key={r.package_id} className="border-t border-slate-100 hover:bg-slate-50/60"
                          data-testid={`care-price-row-${r.package_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{r.name}</div>
                            <div className="text-xs text-slate-400">
                              {r.validity_days}d
                              {r.customer_price == null && <Badge className="ml-2 bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Not published</Badge>}
                              {r.customer_price != null && <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Live</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-600 font-medium">₹{r.your_cost.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <span className="text-slate-400">₹</span>
                              <Input value={draft ?? ''} onChange={e => setDraft(r.package_id, e.target.value)}
                                inputMode="decimal" placeholder="—"
                                className="w-24 h-8 text-right" data-testid={`care-price-input-${r.package_id}`} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {draftMargin == null ? <span className="text-slate-300">—</span> : (
                              <span className={draftMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                ₹{draftMargin.toFixed(2)}
                                <span className="text-xs text-slate-400 ml-1">({marginPct.toFixed(0)}%)</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" onClick={() => saveOne(r.package_id)}
                              data-testid={`care-price-save-${r.package_id}`}>Save</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
